// בלוק app (10, 2026-10-13): HTML+JS של אייבי רץ בדפדפן. iframe sandbox="allow-scripts allow-downloads" — בלי
// allow-same-origin: origin אטום, אין גישה לעוגיות/ל-storage/ל-DOM של הדף המארח. ה-CSP מתיר סקריפטים, סגנונות, גופנים
// ותמונות מ-https (ספריות) ומהמקור שלנו (echarts), ואינו מתיר בקשות רשת (connect-src 'none') — הנתונים מוזרקים.
// לפני קוד אייבי נטען app-runtime.js (window.ab: data, chart, table, fmt, tokens). גובה ושגיאות חוזרים ב-postMessage.
// 16: ab.call — ה-iframe שולח {ab:'call'} ב-postMessage; הדף מזהה אותו לפי event.source (ל-origin אטום אין origin),
// מוסיף זהות (הגשר — AppCallContext, JWT של המשתמש) ושולח ל-POST /api/runs/:runId/calls/:action; התשובה {ab:'result'} חוזרת
// רק ל-iframe הזה. ≤ 8 קריאות פתוחות לבלוק; עזיבת המסך/החלפת הבלוק → abort. חימום (warm) כשהבלוק נטען.
// app@2 (apps-app2-cached-calls): {ab:'call', fresh} → עוקף מטמון; תשובה עם meta → שורה מינימלית "נכון ל- · חושב לפני" על
// הקריאות שנעשו בטעינה (LOAD_WINDOW_MS); {ab:'peek'} → רק מהמטמון (ab.callMeta); {ab:'freshness-own'} — האפליקציה מציגה בעצמה.
import { BlockHead } from './Expand.tsx';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { AppBlock as AppBlockT } from '@aibify/core';
import { pickRunApp } from './screen-model.ts';
import { APP_SANDBOX, appLinkTarget, appSrcDoc, THEME_VARS, type AppTheme } from './app-doc.ts';
import { AppCallContext, useDisplay, type AppCallMeta } from './context.tsx';
import { fmtAgo, fmtAsOf, loadFreshness } from './format.ts';
import { downloadBlob } from './png.ts';
import { S } from './strings.ts';

/** שם קובץ מה-iframe (23 §6) — בלי נתיב/תווי בקרה; ריק → "קובץ". */
export function safeFileName(name: unknown): string {
  // eslint-disable-next-line no-control-regex -- תווי בקרה בשם קובץ
  const s = typeof name === 'string' ? name.replace(/[\\/\u0000-\u001f\u007f]/g, '').trim().slice(0, 200) : '';
  return s || 'קובץ';
}

/** קריאות פתוחות לבלוק (16 §3). */
export const MAX_OPEN_CALLS = 8;
/** app@2: קריאות שנשלחו בחלון הזה מטעינת המסמך = "קריאות הטעינה" לשורת המטמון של המארח. */
export const LOAD_WINDOW_MS = 10_000;

export function readTheme(): AppTheme {
  const cs = getComputedStyle(document.documentElement);
  const vars: Record<string, string> = {};
  for (const n of THEME_VARS) {
    const x = cs.getPropertyValue(n).trim();
    if (x) vars[n] = x;
  }
  const scheme = cs.colorScheme || '';
  const dark = scheme.includes('dark') || document.documentElement.dataset.theme === 'dark' || document.documentElement.classList.contains('dark');
  return { vars, dark };
}

function themeKey(): string {
  const el = document.documentElement;
  return `${el.dataset.theme ?? ''}|${el.className}|${window.matchMedia('(prefers-color-scheme: dark)').matches}`;
}

/** בלוק app: עם קוד — ה-iframe; רק source.runId (בלי html/js/tsx) — הקוד נטען מפלט הריצה, בהרשאת הצופה. */
export function AppBlock({ block }: { block: AppBlockT }) {
  const runId = block.source?.runId;
  if (runId && !block.html && !block.js && !block.tsx) return <RunApp block={block} runId={runId} />;
  return <AppFrame block={block} />;
}

function RunApp({ block, runId }: { block: AppBlockT; runId: string }) {
  const bridge = useContext(AppCallContext);
  const bridgeRef = useRef(bridge);
  bridgeRef.current = bridge;
  const [st, setSt] = useState<{ app: AppBlockT } | { missing: true } | null>(null);
  useEffect(() => {
    let off = false;
    setSt(null);
    const load = bridgeRef.current?.runOutput;
    if (!load) {
      setSt({ missing: true });
      return;
    }
    load(runId).then(
      (out) => {
        if (off) return;
        const app = pickRunApp(out, block.id);
        setSt(app ? { app } : { missing: true });
      },
      () => !off && setSt({ missing: true }),
    );
    return () => {
      off = true;
    };
  }, [runId, block.id]);
  // מה שאייבי נתנה (id, כותרת, גובה) גובר; הקוד והנתונים — של הריצה; source נשאר הריצה (ab.call הולך אליה)
  const merged = useMemo(
    () => (st && 'app' in st ? { ...st.app, id: block.id, titleHe: block.titleHe ?? st.app.titleHe, height: block.height ?? st.app.height, source: { runId } } : null),
    [st, block, runId],
  );
  if (merged) return <AppFrame block={merged} />;
  return (
    <section className="ab-block" data-block-type="app" data-block-id={block.id} data-app-run={runId}>
      <BlockHead titleHe={block.titleHe} />
      {st ? (
        <div className="ab-notice ab-notice-error" role="alert" data-testid="app-run-missing">
          {S.appRunMissing}
        </div>
      ) : (
        <div className="ab-notice ab-notice-info" role="status">
          {S.appRunLoading}
        </div>
      )}
    </section>
  );
}

function AppFrame({ block }: { block: AppBlockT }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [theme, setTheme] = useState(themeKey);
  const [height, setHeight] = useState(block.height ?? 420);
  const [errors, setErrors] = useState<string[]>([]);
  const bridge = useContext(AppCallContext);
  const runId = block.source?.runId ?? null;
  const canCall = !!(bridge && runId);
  const [consentId, setConsentId] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  // app@2: meta של קריאות הטעינה (לפי action+args); האפליקציה עם ab.freshness משלה → השורה של המארח מוסתרת
  const [loadMetas, setLoadMetas] = useState<Record<string, AppCallMeta>>({});
  const [ownFreshness, setOwnFreshness] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const bridgeRef = useRef(bridge);
  bridgeRef.current = bridge;
  // 23 §6: ab.openFile → GET /api/files/:id עם ה-token של המשתמש (אותו fetchFile כמו FileBlock); 404 → הודעה, בלי הורדה
  const { fetchFile } = useDisplay();
  const fetchFileRef = useRef(fetchFile);
  fetchFileRef.current = fetchFile;
  // חימום הסשן כשהבלוק נטען — הלחיצה הראשונה חמה
  useEffect(() => {
    if (canCall && runId) bridgeRef.current?.warm?.(runId);
  }, [canCall, runId]);
  useEffect(() => {
    const upd = () => setTheme(themeKey());
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', upd);
    const mo = new MutationObserver(upd);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => {
      mq.removeEventListener('change', upd);
      mo.disconnect();
    };
  }, []);
  const hasLoadMeta = Object.keys(loadMetas).length > 0;
  useEffect(() => {
    if (!hasLoadMeta) return;
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [hasLoadMeta]);
  useEffect(() => {
    setErrors([]);
    setLoadMetas({});
    setOwnFreshness(false);
    const loadedAt = Date.now();
    const loadKeys = new Set<string>();
    const open = new Map<string, AbortController>();
    const reply = (callId: string, r: { ok: boolean; result?: unknown; error?: unknown; meta?: AppCallMeta }) => {
      // רק ל-iframe הזה (origin אטום → '*'); אם הוחלף בינתיים — אין למי לענות
      ref.current?.contentWindow?.postMessage({ ab: 'result', callId, ...r }, '*');
    };
    const on = (e: MessageEvent) => {
      if (!ref.current || e.source !== ref.current.contentWindow) return;
      const m = e.data as { ab?: string; h?: unknown; message?: unknown; callId?: unknown; action?: unknown; args?: unknown; fileId?: unknown; name?: unknown; fresh?: unknown };
      if (m?.ab === 'height' && typeof m.h === 'number' && !block.height) setHeight(Math.min(6000, Math.max(80, Math.ceil(m.h) + 4)));
      else if (m?.ab === 'file' && typeof m.fileId === 'string' && /^[A-Za-z0-9-]{1,80}$/.test(m.fileId)) {
        const ff = fetchFileRef.current;
        const name = safeFileName(m.name);
        if (!ff) setFileError(S.appFileNone);
        else {
          setFileError(null);
          ff(m.fileId).then(
            (b) => downloadBlob(b, name),
            () => setFileError(S.appFileFailed),
          );
        }
      }
      else if (m?.ab === 'link') {
        // c95e2dad: קישור מה-iframe — רק מהרשימה; הלחיצה ב-iframe מפעילה גם את הדף (user activation עוברת להורים)
        const t = appLinkTarget((m as { href?: unknown }).href);
        if (t?.mode === 'tab') window.open(t.url, '_blank', 'noopener,noreferrer');
        else if (t) window.location.href = t.url;
      }
      else if (m?.ab === 'error') setErrors((xs) => (xs.length >= 5 ? xs : [...xs, String(m.message ?? '').slice(0, 500)]));
      else if (m?.ab === 'abort' && typeof m.callId === 'string') {
        open.get(m.callId)?.abort();
        open.delete(m.callId);
      } else if (m?.ab === 'freshness-own') setOwnFreshness(true);
      else if (m?.ab === 'peek' && typeof m.callId === 'string' && typeof m.action === 'string') {
        // ab.callMeta: רק מהמטמון — בלי ריצה (תצוגה בלי source → null, בלי רשת)
        const callId = m.callId.slice(0, 80);
        const b = bridgeRef.current;
        if (!b?.peek || !runId) return reply(callId, { ok: false });
        b.peek(runId, m.action, m.args ?? {}).then(
          (r) => reply(callId, r ? { ok: true, result: r.result, meta: r.meta } : { ok: false }),
          () => reply(callId, { ok: false }),
        );
      } else if (m?.ab === 'call' && typeof m.callId === 'string' && typeof m.action === 'string') {
        const callId = m.callId.slice(0, 80);
        const b = bridgeRef.current;
        if (!b || !runId) return reply(callId, { ok: false, error: { code: 'unavailable', textHe: S.appNoCall } });
        if (open.size >= MAX_OPEN_CALLS) return reply(callId, { ok: false, error: { code: 'rate_limited', textHe: S.appTooMany } });
        const ac = new AbortController();
        open.set(callId, ac);
        const key = `${m.action} ${JSON.stringify(m.args ?? {})}`;
        if (Date.now() - loadedAt < LOAD_WINDOW_MS) loadKeys.add(key);
        b.call(runId, m.action, m.args ?? {}, ac.signal, m.fresh === true ? { fresh: true } : undefined)
          .then((r) => {
            if (ac.signal.aborted) return;
            if (!r.ok && r.error.code === 'consent_pending' && r.error.consentId) setConsentId(r.error.consentId);
            if (r.ok && r.meta && loadKeys.has(key)) setLoadMetas((x) => ({ ...x, [key]: r.meta! }));
            reply(callId, r.ok ? { ok: true, result: r.result, ...(r.meta ? { meta: r.meta } : {}) } : { ok: false, error: r.error });
          })
          .catch(() => !ac.signal.aborted && reply(callId, { ok: false, error: { code: 'unavailable', textHe: S.appNoCall } }))
          .finally(() => open.delete(callId));
      }
    };
    window.addEventListener('message', on);
    return () => {
      window.removeEventListener('message', on);
      // עזיבת המסך / החלפת הבלוק → ביטול הבקשות הפתוחות (ה-api מבטל → הקונטיינר)
      for (const ac of open.values()) ac.abort();
      open.clear();
    };
  }, [block, runId]);
  // theme ב-deps: החלפת ערכה בונה את המסמך מחדש (צבעים חדשים לקוד של אייבי)
  const doc = useMemo(() => {
    const th = readTheme();
    return { srcDoc: appSrcDoc(block, th, window.location.origin, undefined, { canCall }), dark: th.dark };
  }, [block, theme, canCall]);
  const fresh = loadFreshness(Object.values(loadMetas));
  return (
    <section className="ab-block" data-block-type="app" data-block-id={block.id} data-app-errors={errors.length || undefined} data-can-call={canCall ? '1' : undefined}>
      <BlockHead titleHe={block.titleHe} />
      {fresh && !ownFreshness && (
        <div className="ab-app-freshness" data-testid="app-freshness" role="status">
          {fresh.asOf && <span>{fmtAsOf(fresh.asOf)}</span>}
          {fresh.asOf && ' · '}
          <span>{S.appComputedAgo(fmtAgo(fresh.computedAt, now))}</span>
          {fresh.recomputing && <span> · {S.appRecomputing}</span>}
        </div>
      )}
      {consentId && (
        <div className="ab-notice ab-notice-info" role="status" data-testid="app-consent" data-consent-id={consentId}>
          <strong>{S.appConsent}</strong>{' '}
          {bridge?.openConsent && (
            <button type="button" className="ab-link" onClick={() => bridge.openConsent!(consentId)}>
              {S.appConsentOpen}
            </button>
          )}
        </div>
      )}
      {fileError && (
        <div className="ab-notice ab-notice-error" role="alert" data-testid="app-file-error">
          {fileError}
        </div>
      )}
      {errors.length > 0 && (
        <div className="ab-notice ab-notice-error" role="alert">
          <strong>{S.appError}:</strong> {errors[0]}
        </div>
      )}
      <iframe
        ref={ref}
        className="ab-app"
        title={block.titleHe ?? S.appTitle}
        sandbox={APP_SANDBOX}
        referrerPolicy="no-referrer"
        srcDoc={doc.srcDoc}
        // אותו color-scheme כמו המסמך שבפנים — אחרת הדפדפן צובע את ה-iframe ברקע אטום (שחור בערכה הכהה)
        style={{ blockSize: `${height}px`, colorScheme: doc.dark ? 'dark' : 'light' }}
      />
    </section>
  );
}
